import React, { useEffect, useRef, useState } from 'react';
import styled from 'App/styled';
import { ActiveFilter, ListingFilter } from '@nykaa/listing-shared/type/filter';
import ArrowIcon from 'components/Icons/Arrow';
import CrossIcon from 'components/Icons/CircleCross';
import { getSelectedFilterCount } from 'helpers/filters';
import {
  firstFilterIdName,
  lastFilterIdName,
} from 'components/ListingPage/Scroll/constants';
import Facets from '../Facets';

const FilterTabWrapper = styled.div`
  border-bottom: solid 1px #ebebeb;
  padding: 0 5px 0 12px;
  &:last-of-type {
    border: none;
  }
  #custom-scroll::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    background-color: #f5f5f5;
  }

  #custom-scroll::-webkit-scrollbar {
    width: 4px;
    background-color: #f5f5f5;
  }

  #custom-scroll::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #gray;
  }
`;

const SearchBox = styled.input`
  background-color: ${({ theme }) => theme.colors.snow400};
  padding-left: 12px;
  margin: 8px 0;
  color: ${({ theme }) => theme.colors.surfaceInverse};
  height: 38px;
  border-radius: 3px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  ${({ theme }) => theme.typography.bodyMedium};
  &::placeholder {
    color: ${({ theme }) => theme.colors.surfaceInverse};
  }
`;

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const Icon = styled.div`
  width: 20px;
  height: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilterOption = styled.div`
  cursor: pointer;
  padding: 14px 0;
  padding-right: 17px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .filter-action {
    display: flex;
    align-items: center;
  }
  .cross-icon,
  .arrow-icon {
    fill: #5c6874;
  }
  .filter-count {
    margin-right: 16px;
    ${({ theme }) => theme.typography.subTitleMedium};
    &.active {
      color: ${(props) => props.theme.brandPrimary};
    }
  }
  .title {
    text-transform: capitalize;
    ${({ theme }) => theme.typography.subTitleLarge};
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

interface TabsProps {
  handleChange: (e: ActiveFilter) => void;
  filter: ListingFilter;
  isFirst: boolean;
  isLast: boolean;
  activeFilters: { [key: string]: ActiveFilter | number | string };
}

const Tabs = (props: TabsProps) => {
  const { filter, activeFilters, handleChange, isFirst, isLast, ...rest } =
    props;
  const [showFacet, setShowFacet] = useState(false);
  const [newFilter, setNewFilter] = useState({ ...filter });
  const [searchText, setSearchText] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [suggestionsCount, setSuggestionsCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    setNewFilter({ ...filter });
  }, [filter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const text = `${searchText} with ${suggestionsCount} ${
        suggestionsCount === 1 ? 'item' : 'items'
      } available`;
      setAnnouncement(text);
    }, 1300);
    return () => clearTimeout(timer);
  }, [suggestionsCount, searchText]);

  if (!newFilter || !newFilter.key) {
    return null;
  }
  const handleFacets = () => {
    setShowFacet(!showFacet);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value || '';
    const data = [...filter.data];
    if (ref.current) {
      ref.current.focus();
    }

    const searchData = data.filter((value) => {
      const name = value?.name || '';
      return name.toLowerCase().includes(searchValue.toLowerCase());
    });
    newFilter.data = searchData;
    setNewFilter({ ...newFilter });
    setSearchText(searchValue);
    setSuggestionsCount(searchData.length);
  };
  const filterCount = getSelectedFilterCount([newFilter], activeFilters);
  const showSearch = newFilter?.showSearch || false;
  const showChildren =
    showFacet &&
    ((newFilter.data &&
      (newFilter.data.length > 0 || Object.keys(newFilter.data).length)) ||
      searchText);
  const titleActive = showChildren ? 'active' : '';
  const appliedCount = `${filterCount}`;
  const activeFilter =
    (activeFilters[newFilter?.key] as ActiveFilter)?.data || [];
  const id = isFirst ? firstFilterIdName : isLast ? lastFilterIdName : '';
  return (
    <FilterTabWrapper
      id={id}
      key={`${newFilter.key}_filter`}
    >
      <FilterOption
        key={newFilter.title}
        className="filter-open"
        onClick={handleFacets}
      >
        <span className={`title ${titleActive}`}>{newFilter.title}</span>
        <div className="filter-action">
          {filterCount > 0 && (
            <span className={`filter-count ${titleActive}`}>
              {appliedCount}
            </span>
          )}
          <Icon>{showChildren ? <CrossIcon /> : <ArrowIcon />}</Icon>
        </div>
      </FilterOption>
      {showSearch && showChildren && (
        <SearchBox
          ref={ref}
          key={`search-${newFilter.title}`}
          type="text"
          placeholder="Search"
          onChange={onSearch}
          value={searchText}
        />
      )}
      {showChildren && (
        <Facets
          handleFilterChange={handleChange}
          activeFilters={activeFilter}
          filter={newFilter}
          {...rest}
        />
      )}
      <VisuallyHidden
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </VisuallyHidden>
    </FilterTabWrapper>
  );
};

export default Tabs;
